import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Search, User, Music } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useLookup } from "@/hooks/useLookup";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function AddArtistDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [genre, setGenre] = useState("");
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showList, setShowList] = useState(false);
  const { results: artists, loading } = useLookup("artists", name);
  const spotTimer = useRef();

  // Prominent artist image from Spotify oEmbed (keyless), degrades silently.
  useEffect(() => {
    clearTimeout(spotTimer.current);
    if (!spotifyUrl.includes("open.spotify.com/artist")) return;
    spotTimer.current = setTimeout(async () => {
      setSpotifyLoading(true);
      try {
        const { data } = await api.get("/lookup/spotify", { params: { url: spotifyUrl } });
        if (data?.data?.image) {
          setImage(data.data.image);
          if (!name.trim() && data.data.title) setName(data.data.title);
          toast.success("Spotify artist image loaded");
        }
      } catch { /* ignore */ }
      finally { setSpotifyLoading(false); }
    }, 600);
    return () => clearTimeout(spotTimer.current);
  }, [spotifyUrl]);

  const pickArtist = async (a) => {
    setName(a.artist);
    if (a.genre) setGenre(a.genre);
    setShowList(false);
    // artist images aren't in iTunes artist results — grab representative artwork from a song
    try {
      const { data } = await api.get("/lookup/songs", { params: { term: a.artist } });
      const art = (data.results || []).find((r) => r.artwork)?.artwork;
      if (art) setImage(art);
    } catch { /* ignore */ }
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter an artist name");
    setSaving(true);
    try {
      const { data } = await api.post("/artists", { name: name.trim(), image: image.trim() });
      toast.success("Artist added");
      onOpenChange(false);
      setName(""); setImage(""); setGenre(""); setSpotifyUrl("");
      onCreated?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-artist-dialog" className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add Artist</DialogTitle>
          <DialogDescription>Search to auto-fill canonical name, artwork and genre.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Label className="flex items-center gap-1"><Search className="h-3 w-3" /> Artist name</Label>
            <Input
              data-testid="input-artist-name"
              value={name}
              onChange={(e) => { setName(e.target.value); setShowList(true); }}
              onFocus={() => setShowList(true)}
              onBlur={() => setTimeout(() => setShowList(false), 150)}
              className="mt-1"
              autoComplete="off"
              placeholder="e.g. Brent Faiyaz"
            />
            {showList && (loading || artists.length > 0) && (
              <div data-testid="artist-suggestions" className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-neutral-200 bg-white p-1 shadow-xl">
                {loading && <div className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</div>}
                {artists.map((a, i) => (
                  <button
                    key={i}
                    data-testid={`artist-suggestion-${i}`}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pickArtist(a); }}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-neutral-100"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-neutral-100"><User className="h-4 w-4 text-neutral-400" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{a.artist}</span>
                      {a.genre && <span className="block truncate text-xs text-neutral-500">{a.genre}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {image && (
            <div data-testid="artist-image-preview" className="relative overflow-hidden rounded-2xl border border-neutral-200">
              <img src={image} alt="artist" className="h-44 w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-sm font-semibold text-white">{name || "Artist"}</p>
                {genre && <p className="text-xs text-white/70">{genre}</p>}
              </div>
            </div>
          )}
          <div>
            <Label className="flex items-center gap-1"><Music className="h-3 w-3" /> Spotify artist link (optional)</Label>
            <div className="relative">
              <Input
                data-testid="input-spotify-url"
                value={spotifyUrl}
                onChange={(e) => setSpotifyUrl(e.target.value)}
                className="mt-1"
                placeholder="https://open.spotify.com/artist/..."
              />
              {spotifyLoading && <Loader2 className="absolute right-3 top-4 h-4 w-4 animate-spin text-neutral-400" />}
            </div>
            <p className="mt-1 text-xs text-neutral-400">Pulls the artist's high-res Spotify photo for a prominent profile.</p>
          </div>
          <div>
            <Label>Image URL (optional)</Label>
            <Input data-testid="input-artist-image" value={image} onChange={(e) => setImage(e.target.value)} className="mt-1" placeholder="https://..." />
          </div>
          <Button data-testid="submit-add-artist" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#35606E] py-6 hover:bg-[#294b57]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add Artist
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
